import React, { useMemo } from "react";
import "./HowWeWork.css";

const fallbackHowWeWork = {
    tag: "HOW IT WORKS",
    title: "How we work with you",
    item1Number: "01",
    item1Title: "Consulting",
    item1Text:
        "Site inspections and technical evaluations to determine the right drywall system for your project — fully aligned with international standards.",
    item2Number: "02",
    item2Title: "Technical Proposal",
    item2Text:
        "Integrated system design, material supply, and technical support — from specification to execution, with fire, thermal, and acoustic performance built in.",
    item3Number: "03",
    item3Title: "Delivery & warranty",
    item3Text:
        "Reliable delivery through authorized distributors. Full warranty on products installed according to approved IRQGYP specifications and methods.",
    item4Number: "04",
    item4Title: "Training engineers",
    item4Text:
        "Professional training programs — webinars, online sessions, and hands-on workshops — covering products, systems, and best installation practices.",
};

const fallbackSteps = [
    {
        number: fallbackHowWeWork.item1Number,
        title: fallbackHowWeWork.item1Title,
        text: fallbackHowWeWork.item1Text,
    },
    {
        number: fallbackHowWeWork.item2Number,
        title: fallbackHowWeWork.item2Title,
        text: fallbackHowWeWork.item2Text,
    },
    {
        number: fallbackHowWeWork.item3Number,
        title: fallbackHowWeWork.item3Title,
        text: fallbackHowWeWork.item3Text,
    },
    {
        number: fallbackHowWeWork.item4Number,
        title: fallbackHowWeWork.item4Title,
        text: fallbackHowWeWork.item4Text,
    },
];

const itemFieldRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;

const tryParseJsonObject = (str) => {
    const t = String(str || "").trim();
    if (!t.startsWith("{") || !t.endsWith("}")) return null;
    try {
        const o = JSON.parse(t);
        return o && typeof o === "object" && !Array.isArray(o) ? o : null;
    } catch {
        return null;
    }
};

const coalesceHowPayload = (raw) => {
    if (raw == null) return null;
    if (typeof raw === "string") {
        return tryParseJsonObject(raw) || {};
    }
    if (typeof raw !== "object") return null;

    let base = { ...raw };
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
        base = { ...raw, ...raw.data };
    }
    if (typeof raw.value === "string") {
        const parsed = tryParseJsonObject(raw.value);
        if (parsed) base = { ...base, ...parsed };
    } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
        base = { ...base, ...raw.value };
    }
    return base;
};

const pickString = (...candidates) => {
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return "";
};

const hasMeaningfulHowPayload = (c) => {
    if (!c || typeof c !== "object") return false;
    if (pickString(c.tag, c.sectionTag, c.title, c.heading, c.titleLine1)) return true;
    return Object.keys(c).some((k) => itemFieldRegex.test(k));
};

const HowWeWork = ({ content }) => {
    const section = useMemo(() => {
        const payload = coalesceHowPayload(content);
        if (!hasMeaningfulHowPayload(payload) || !payload) return fallbackHowWeWork;
        return payload;
    }, [content]);

    const steps = useMemo(() => {
        const cardsMap = new Map();
        Object.entries(section || {}).forEach(([key, value]) => {
            const match = key.match(itemFieldRegex);
            if (!match) return;
            const index = Number(match[1]);
            const field = String(match[2] || "").replace(/[_\s-]+/g, "").toLowerCase();
            if (!cardsMap.has(index)) cardsMap.set(index, {});
            cardsMap.get(index)[field] = value;
        });

        const mapped = Array.from(cardsMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([index, fields], idx) => {
                const fallback = fallbackSteps[idx % fallbackSteps.length];
                const defaultNumber = String(index).padStart(2, "0");
                return {
                    number: pickString(fields.number, fields.step, fields.index) || defaultNumber || fallback.number,
                    title: pickString(fields.title, fields.heading) || fallback.title,
                    text: pickString(fields.text, fields.description, fields.body) || fallback.text,
                };
            })
            .filter((item) => item.number || item.title || item.text);

        return mapped.length ? mapped : fallbackSteps;
    }, [section]);

    return (
        <section className="howwework">
            <div className="howwework-container">

                <div className="howwework-top">
                    <span className="howwework-tag">
                        {pickString(section.tag, section.sectionTag) || fallbackHowWeWork.tag}
                    </span>
                    <h2>
                        {pickString(
                            section.title,
                            section.heading,
                            [section.titleLine1, section.titleLine2].filter(Boolean).join(" ").trim()
                        ) || fallbackHowWeWork.title}
                    </h2>
                </div>

                <div className="howwework-divider"></div>

                <div className="howwework-grid">
                    {steps.map((step, index) => (
                        <div className="howwework-card" key={`${step.number}-${step.title}-${index}`}>
                            <span className="howwework-number">{step.number}</span>
                            <div className="howwework-bot">
                                <h3>{step.title}</h3>
                                <p>{step.text}</p>
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default HowWeWork;